import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API, authedHeaders } from "@/hooks/useAuth";
import { ShieldCheck } from "lucide-react";

export function VerifyAge() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const refreshMe = refresh;
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Bounce to lobby in a side effect (never during render) once we detect
  // the user is already age-verified.
  useEffect(() => {
    if (user?.age_verified) navigate("/lobby", { replace: true });
  }, [user?.age_verified, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/verify-age`,
        { date_of_birth: dob },
        { headers: authedHeaders() }
      );
      await refreshMe?.();
      navigate("/lobby", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Layout>
      <section className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Safety first</p>
        <h1 className="font-display text-4xl mt-1 flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-emerald-600" /> Verify your age
        </h1>
        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
          To use the lobby, chat, or play live against another person, we need
          your date of birth. We use this to keep players in the right
          age-appropriate rooms:
        </p>
        <ul className="text-sm mt-3 space-y-1.5 list-disc pl-5 text-foreground/80">
          <li><strong>Under 13</strong> — kids-safe zone, canned phrases only, no free-text chat.</li>
          <li><strong>13–17</strong> — chat &amp; play with other teens; you can invite anyone by exact username.</li>
          <li><strong>18+</strong> — chat &amp; play with other adults; you can invite anyone by exact username.</li>
        </ul>

        <Card className="mt-8 p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="dob">Your date of birth</Label>
              <Input
                id="dob"
                data-testid="verify-age-dob"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={today}
                required
              />
            </div>
            {error && <p data-testid="verify-age-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="verify-age-submit" type="submit" disabled={busy} className="w-full rounded-full">
              {busy ? "Saving…" : "Confirm age"}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              By submitting you confirm this is your real date of birth.
              Misrepresentation may result in your account being suspended.
            </p>
          </form>
        </Card>
      </section>
    </Layout>
  );
}
